/**
 * FunnelEditorDialog — Create / Edit a support funnel with its stages.
 * Stages can be added, renamed, reordered (up/down arrows), and removed.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SupportFunnel } from './types';
import { apiService } from '../../services/api';

const DEEP_TEAL = '#1A5F7A';
const WARM_GOLD = '#D4AC0D';

const PRESET_COLORS = [
  '#1A5F7A', '#D4AC0D', '#146152', '#7D1935', '#5D6D7E',
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
];

interface Stage {
  name: string;
  color: string;
}

interface FunnelEditorDialogProps {
  open: boolean;
  funnel: SupportFunnel | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (id: string) => void;
}

const FunnelEditorDialog: React.FC<FunnelEditorDialogProps> = ({
  open,
  funnel,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(DEEP_TEAL);
  const [stages, setStages] = useState<Stage[]>([]);
  const [newStageName, setNewStageName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (funnel) {
      setName(funnel.name);
      setDescription(funnel.description || '');
      setColor(funnel.color || DEEP_TEAL);
      setStages(funnel.stages.map(s => ({ name: s.name, color: s.color || DEEP_TEAL })));
    } else {
      setName('');
      setDescription('');
      setColor(DEEP_TEAL);
      setStages([
        { name: 'New', color: DEEP_TEAL },
        { name: 'In Progress', color: WARM_GOLD },
        { name: 'Resolved', color: '#146152' },
      ]);
    }
    setNewStageName('');
    setError(null);
  }, [open, funnel]);

  const addStage = () => {
    const trimmed = newStageName.trim();
    if (!trimmed) return;
    setStages(prev => [...prev, { name: trimmed, color: DEEP_TEAL }]);
    setNewStageName('');
  };

  const removeStage = (idx: number) => {
    setStages(prev => prev.filter((_, i) => i !== idx));
  };

  const renameStageName = (idx: number, name: string) => {
    setStages(prev => prev.map((s, i) => i === idx ? { ...s, name } : s));
  };

  const moveStage = (idx: number, dir: -1 | 1) => {
    setStages(prev => {
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Funnel name is required'); return; }
    if (stages.length === 0) { setError('At least one stage is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const stagePayload = stages.map((s, i) => ({ name: s.name, stageOrder: i + 1, color: s.color }));
      if (funnel) {
        await apiService.updateSupportFunnel(funnel.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          color,
          stages: stagePayload,
        });
      } else {
        await apiService.createSupportFunnel({
          name: name.trim(),
          description: description.trim() || undefined,
          color,
          stages: stagePayload,
        });
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to save funnel');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!funnel) return;
    setDeleting(true);
    try {
      await apiService.deleteSupportFunnel(funnel.id);
      onDeleted(funnel.id);
    } catch (err: any) {
      setError(err.message || 'Failed to delete funnel');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, borderTop: `3px solid ${DEEP_TEAL}` } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: DEEP_TEAL }}>
          {funnel ? 'Edit Funnel' : 'New Support Funnel'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          size="small"
          label="Funnel name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 1.5 }}
          required
        />

        <TextField
          fullWidth
          size="small"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Color picker */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <Box
                key={c}
                onClick={() => setColor(c)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  border: color === c ? '3px solid rgba(0,0,0,0.4)' : '2px solid transparent',
                  boxSizing: 'border-box',
                  transition: 'border 0.15s',
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Stages */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: DEEP_TEAL }}>
          Stages
        </Typography>

        <List disablePadding dense>
          {stages.map((stage, idx) => (
            <ListItem
              key={idx}
              disablePadding
              sx={{ mb: 0.5, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Chip
                label={idx + 1}
                size="small"
                sx={{ width: 26, height: 26, bgcolor: stage.color, color: '#fff', fontWeight: 700, fontSize: '0.7rem', '.MuiChip-label': { px: 0 }, flexShrink: 0 }}
              />
              <TextField
                size="small"
                value={stage.name}
                onChange={(e) => renameStageName(idx, e.target.value)}
                variant="standard"
                sx={{ flex: 1, '& .MuiInput-input': { fontSize: '0.85rem', fontWeight: 500, py: 0.25 } }}
                inputProps={{ 'aria-label': `Stage ${idx + 1} name` }}
              />
              <IconButton size="small" onClick={() => moveStage(idx, -1)} disabled={idx === 0} sx={{ p: 0.25 }}>
                <UpIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" onClick={() => moveStage(idx, 1)} disabled={idx === stages.length - 1} sx={{ p: 0.25 }}>
                <DownIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" onClick={() => removeStage(idx)} sx={{ p: 0.25, color: 'error.main' }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </ListItem>
          ))}
        </List>

        {/* Add stage input */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            placeholder="New stage name"
            value={newStageName}
            onChange={(e) => setNewStageName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStage(); } }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addStage}
            disabled={!newStageName.trim()}
            sx={{ textTransform: 'none', borderColor: DEEP_TEAL, color: DEEP_TEAL }}
          >
            Add
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {funnel && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleDelete}
            disabled={deleting || saving}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete Funnel'}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving || deleting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || deleting || !name.trim()}
          sx={{ bgcolor: DEEP_TEAL, '&:hover': { bgcolor: '#154e63' }, textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : (funnel ? 'Save Changes' : 'Create Funnel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FunnelEditorDialog;
